import { Component, Inject } from '@angular/core';
import { MatDatepicker } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { LocalStorageService } from 'src/app/core/services/local-storage.service';

@Component({
  selector: 'app-date-range-picker',
  templateUrl: './date-range-picker.component.html',
  styleUrls: ['./date-range-picker.component.css'],
})
export class DateRangePickerComponent {
  constructor(
    public dialogRef: MatDialogRef<DateRangePickerComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private localStorageService: LocalStorageService,
  ) {}

  startDate: Date | null = new Date();
  endDate: Date | null = new Date();
  maxDate = new Date();
  monthly = false;
  allowRangeToggle = false;
  isDateRange = true;

  public currentLang: string = this.localStorageService.get('language') || 'en';

  ngOnInit() {
    this.monthly = !!this.data?.monthly;
    this.allowRangeToggle =
      this.data?.allowRangeToggle !== undefined
        ? !!this.data.allowRangeToggle
        : this.monthly;
    this.isDateRange =
      this.data?.isDateRange !== undefined ? !!this.data.isDateRange : true;
    this.startDate = this.normalizeDate(this.data?.startDate) || new Date();
    this.endDate = this.normalizeDate(this.data?.endDate);

    if (this.monthly) {
      this.startDate = this.toMonthStart(this.startDate || new Date());
      this.endDate = this.endDate ? this.toMonthStart(this.endDate) : null;

      if (!this.isDateRange) {
        this.endDate = null;
      }
    }
  }

  onCancel() {
    this.dialogRef.close();
  }

  onApply() {
    this.dialogRef.close({
      startDate: this.startDate,
      endDate: this.isDateRange ? this.endDate : null,
      isDateRange: this.isDateRange,
      monthly: this.monthly,
    });
  }

  canApply(): boolean {
    if (!this.startDate) return false;

    if (!this.isDateRange) return true;

    return !!this.endDate;
  }

  onStartSelect(date: Date | null) {
    if (date) {
      this.startDate = new Date(date);
      if (this.endDate && this.endDate < this.startDate) {
        this.endDate = null;
      }

      this.refreshCalendars();
    }
  }

  onEndSelect(date: Date | null) {
    if (!this.startDate) return;

    if (date) this.endDate = new Date(date);
    this.refreshCalendars();
  }

  onStartMonthSelected(date: Date, datepicker: MatDatepicker<Date>) {
    const selectedMonth = this.toMonthStart(date);

    this.startDate = selectedMonth;

    if (!this.isDateRange) {
      this.endDate = null;
    } else if (this.endDate && this.endDate < selectedMonth) {
      this.endDate = null;
    }

    datepicker.close();
  }

  onEndMonthSelected(date: Date, datepicker: MatDatepicker<Date>) {
    if (!this.startDate) return;

    const selectedMonth = this.toMonthStart(date);

    if (selectedMonth < this.startDate) {
      this.endDate = null;
      datepicker.close();
      return;
    }

    this.endDate = selectedMonth;
    datepicker.close();
  }

  onDateRangeToggle(checked: boolean) {
    this.isDateRange = checked;

    if (!checked) {
      this.endDate = null;
      return;
    }

    if (!this.endDate && this.startDate) {
      this.endDate = new Date(this.startDate);
    }
  }

  formatSelectedDate(date: Date | null): string {
    if (!date) return '-';

    return this.monthly
      ? new Intl.DateTimeFormat(this.currentLang, {
          month: 'long',
          year: 'numeric',
        }).format(date)
      : new Intl.DateTimeFormat(this.currentLang, {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        }).format(date);
  }

  private normalizeDate(value: any): Date | null {
    if (!value) return null;

    const parsedDate = value instanceof Date ? new Date(value) : new Date(value);

    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  }

  private toMonthStart(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), 1);
  }

  renderCalendars: boolean = true;
  refreshCalendars() {
    this.renderCalendars = false;

    requestAnimationFrame(() => {
      this.renderCalendars = true;
    });
  }

  dateRangeClass = (date: Date) => {
    if (!this.startDate || !this.endDate) return '';

    const d = new Date(date).setHours(0, 0, 0, 0);
    const start = new Date(this.startDate).setHours(0, 0, 0, 0);
    const end = new Date(this.endDate).setHours(0, 0, 0, 0);

    if (start === end && d === start) {
      return 'range-single';
    }

    if (d > start && d < end) return 'in-range';
    if (d === start) return 'range-start';
    if (d === end) return 'range-end';

    return '';
  };
}
